import { useCallback, useEffect } from 'react'

import { useInviteLinksStore } from '@/features/analytics/model'
import {
    AnalyticsTableRowTypeChannels,
    AnalyticsTableRowTypeSites,
} from '@/features/analytics/types'
import { CamelIdObj } from '@synapex/tools/typescript'

export const useInviteLinkSelection = ({ groupId }: CamelIdObj<'group'>) => {
    const {
        selectedLinksInviteByGroup,
        toggleSelectedLinkInviteByGroup,
        clearSelectedLinksInviteByGroup,
    } = useInviteLinksStore()

    const selectedLinksSet = (selectedLinksInviteByGroup[groupId] ?? []).reduce(
        (result, linkData) => {
            result.add(linkData.invite_link_id)
            return result
        },
        new Set<number>()
    )

    const onLinkSelect = useCallback(
        (row: AnalyticsTableRowTypeChannels) => {
            toggleSelectedLinkInviteByGroup(groupId, row.source)
        },
        [groupId, toggleSelectedLinkInviteByGroup]
    )

    useEffect(() => {
        return () => {
            clearSelectedLinksInviteByGroup(groupId)
        }
    }, [groupId, clearSelectedLinksInviteByGroup])

    return {
        selectedLinksSet,
        onLinkSelect,
    }
}

export type UseInviteLinkSelection = ReturnType<typeof useInviteLinkSelection>

export const useSiteLinkSelection = ({ groupId }: CamelIdObj<'group'>) => {
    const {
        selectedSiteLinksByGroup,
        toggleSelectedLinkSiteByGroup,
        clearSelectedSiteLinksByGroup,
    } = useInviteLinksStore()

    const selectedLinksSet = (selectedSiteLinksByGroup[groupId] ?? []).reduce(
        (result, linkData) => {
            result.add(linkData.site_link_id)
            return result
        },
        new Set<number>()
    )

    const onLinkSelect = useCallback(
        (row: AnalyticsTableRowTypeSites) => {
            toggleSelectedLinkSiteByGroup(groupId, row.source)
        },
        [groupId, toggleSelectedLinkSiteByGroup]
    )

    useEffect(() => {
        return () => {
            clearSelectedSiteLinksByGroup(groupId)
        }
    }, [groupId, clearSelectedSiteLinksByGroup])

    return {
        selectedLinksSet,
        onLinkSelect,
    }
}

export type UseSiteLinkSelection = ReturnType<typeof useSiteLinkSelection>
